import { useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { startAuthentication } from '@simplewebauthn/browser';
import { authApi } from './api';
import { useAuthStore } from './store';
import { btnPrimary, fieldLabelCls, inputCls } from '../lib/styles';

/** Глиф Face ID (как в iOS): уголки рамки + глаза, нос, улыбка. */
function FaceIdIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {/* уголки рамки */}
      <path d="M3 7V5a2 2 0 0 1 2-2h2" />
      <path d="M17 3h2a2 2 0 0 1 2 2v2" />
      <path d="M21 17v2a2 2 0 0 1-2 2h-2" />
      <path d="M7 21H5a2 2 0 0 1-2-2v-2" />
      {/* глаза */}
      <path d="M8.5 9v1.5" />
      <path d="M15.5 9v1.5" />
      {/* нос */}
      <path d="M12 9.5v3a0.8 0.8 0 0 1-0.8 0.8" />
      {/* улыбка */}
      <path d="M8.7 15.5a4.5 4.5 0 0 0 6.6 0" />
    </svg>
  );
}

export function LoginPage() {
  const accessToken = useAuthStore((s) => s.accessToken);
  const setSession = useAuthStore((s) => s.setSession);
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  // Двухшаговый вход: после верного пароля при включённом 2FA сервер
  // возвращает mfaToken — показываем поле для 6-значного кода.
  const [mfaToken, setMfaToken] = useState<string | null>(null);
  const [totpCode, setTotpCode] = useState('');

  if (accessToken) {
    const dest = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={dest} replace />;
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const result = await authApi.login(email, password);
      if (result.mfaRequired && result.mfaToken) {
        setMfaToken(result.mfaToken);
      } else if (result.tokens) {
        setSession(result.tokens);
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function submitTotp(e: React.FormEvent) {
    e.preventDefault();
    if (!mfaToken) return;
    setError(null);
    setLoading(true);
    try {
      const tokens = await authApi.loginTotp(mfaToken, totpCode);
      setSession(tokens);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function loginWithPasskey() {
    setError(null);
    setLoading(true);
    try {
      const { options, challengeId } = await authApi.passkeyLoginOptions();
      const credential = await startAuthentication({
        optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
      });
      const tokens = await authApi.passkeyLoginVerify(challengeId, credential);
      setSession(tokens);
    } catch (err) {
      // NotAllowedError = пользователь закрыл системный диалог — не ошибка.
      if ((err as Error).name !== 'NotAllowedError') {
        setError((err as Error).message);
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-bg p-4">
      <form
        onSubmit={mfaToken ? submitTotp : submit}
        className="w-full max-w-sm rounded-lg border border-border bg-bg2 p-6 shadow-sm"
      >
        <h1 className="mb-6 text-2xl font-bold tracking-tight text-text">Wave Panel</h1>

        {!mfaToken ? (
          <>
            <label className="mb-3 block">
              <span className={fieldLabelCls}>Email</span>
              <input
                type="email"
                required
                autoFocus
                autoComplete="username webauthn"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
              />
            </label>

            <label className="mb-5 block">
              <span className={fieldLabelCls}>Пароль</span>
              <input
                type="password"
                required
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
              />
            </label>
          </>
        ) : (
          <label className="mb-5 block">
            <span className={fieldLabelCls}>Код из приложения-аутентификатора</span>
            <input
              type="text"
              inputMode="numeric"
              pattern="\d{6}"
              maxLength={6}
              required
              autoFocus
              placeholder="000000"
              value={totpCode}
              onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
              className={`${inputCls} text-center font-mono text-lg tracking-[0.4em]`}
            />
            <span className="mt-1.5 block text-[11px] text-text-subtle">
              Двухфакторная аутентификация включена для этого аккаунта
            </span>
          </label>
        )}

        {error && (
          <div className="mb-4 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
            {error}
          </div>
        )}

        <button type="submit" disabled={loading} className={`${btnPrimary} w-full`}>
          {loading ? 'Вход…' : mfaToken ? 'Подтвердить' : 'Войти'}
        </button>

        {mfaToken ? (
          <button
            type="button"
            onClick={() => {
              setMfaToken(null);
              setTotpCode('');
              setError(null);
            }}
            className="mt-3 w-full text-center text-xs text-text2 hover:text-text"
          >
            ← Назад к вводу пароля
          </button>
        ) : (
          <>
            <div className="my-4 flex items-center gap-3">
              <div className="h-px flex-1 bg-border" />
              <span className="text-[11px] uppercase tracking-wide text-text-subtle">или</span>
              <div className="h-px flex-1 bg-border" />
            </div>
            <button
              type="button"
              onClick={() => void loginWithPasskey()}
              disabled={loading}
              title="Войти с Passkey"
              aria-label="Войти с Passkey"
              className="flex w-full items-center justify-center rounded-md border border-border bg-bg px-4 py-2.5 text-text transition-colors duration-150 hover:bg-bg3"
            >
              <FaceIdIcon />
            </button>
          </>
        )}
      </form>
    </div>
  );
}
